import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  type InputProps,
  InputRightElement,
} from '@chakra-ui/react';
import { type CustomIcon, TriangleExclamationIcon } from '@ui/components/Icons';
import theme from '@ui/config/theme';
import React, { forwardRef } from 'react';

export interface FieldProps extends InputProps {
  label?: string;
  showLabel?: boolean;
  leftIcon?: CustomIcon;
  rightIcon?: CustomIcon;
  errorIcon?: CustomIcon;
  isInvalid?: boolean;
  error?: string;
  errorSeverity?: 'error' | 'warning' | 'info';
  children?: React.ReactNode;
}

function getErrorIconColor(severity: FieldProps['errorSeverity']) {
  switch (severity) {
    case 'warning':
      return theme.colors.validation.warning;
    case 'info':
      return theme.colors.validation.info;
    case 'error':
    default:
      return theme.colors.validation.error;
  }
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      label,
      showLabel = true,
      leftIcon,
      rightIcon,
      errorIcon = TriangleExclamationIcon,
      isInvalid,
      error,
      errorSeverity,
      visibility,
      isDisabled = false,
      id,
      children,
      ...rest
    },
    ref,
  ) => {
    let LeftIcon = null;

    if (leftIcon) {
      LeftIcon = React.createElement(leftIcon, {
        color: theme.colors.input.icon,
      });
    }

    let RightIcon = null;

    if (rightIcon) {
      RightIcon = React.createElement(rightIcon, {
        color: theme.colors.input.icon,
      });
    }

    let ErrorIcon = null;
    const errorColor = getErrorIconColor(errorSeverity);

    if (errorIcon) {
      ErrorIcon = React.createElement(errorIcon, {
        color: errorColor,
      });
    }

    return (
      <FormControl isInvalid={isInvalid} visibility={visibility}>
        {showLabel && label && (
          <FormLabel fontSize="sm" mb={0} htmlFor={id}>
            {label}
          </FormLabel>
        )}

        <InputGroup>
          {isInvalid ? (
            <InputLeftElement>{ErrorIcon}</InputLeftElement>
          ) : (
            LeftIcon && (
              <InputLeftElement pointerEvents="none">
                {LeftIcon}
              </InputLeftElement>
            )
          )}

          {RightIcon && (
            <InputRightElement pointerEvents="none">
              {RightIcon}
            </InputRightElement>
          )}

          <Input
            autoComplete="no-auto-complete"
            isDisabled={isDisabled}
            ref={ref}
            id={id}
            {...rest}
          />
        </InputGroup>

        <FormErrorMessage color={errorColor} fontSize="small" mt={0}>
          {error}
        </FormErrorMessage>

        {children}
      </FormControl>
    );
  },
);
